import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';

/**
 * Response shape for `POST /v1/clients/{clientId}/licenses/{licenseId}/rotate-engine-credentials`.
 *
 * `engineApiClientSecret` is exposed **once** here. The operator UI is
 * the only place this value should ever be surfaced; never log it,
 * never persist it client-side, never re-render after the modal
 * closes.
 */
export interface RotateEngineCredentialsResponse {
  engineApiClientId: string;
  engineApiClientSecret: string;
}

interface RotateInput {
  clientId: string;
  licenseId: string;
}

/**
 * Mutation hook for ADR 0012 credential rotation. On success we
 * invalidate the licenses list so any UI that displays the
 * `engineApiClientId` re-fetches it (the clientId itself is
 * unchanged, but other rows in the list might have been mutated by
 * unrelated tabs — invalidation is cheap and consistent).
 */
export function useRotateEngineCredentialsMutation(): UseMutationResult<
  RotateEngineCredentialsResponse,
  Error,
  RotateInput
> {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, licenseId }: RotateInput) => {
      if (!api) throw new Error('useRotateEngineCredentialsMutation: api client unavailable');
      return api.post<RotateEngineCredentialsResponse>(
        `/v1/clients/${clientId}/licenses/${licenseId}/rotate-engine-credentials`,
        {},
      );
    },
    onSuccess: (_data, { clientId }) => {
      void queryClient.invalidateQueries({ queryKey: ['licenses', clientId] });
    },
  });
}
